/** @jsxImportSource @emotion/react */

import { FiltersSchema } from '@vitafy/search-data-api-contracts';
import { type CategoryByIDFromDBDto, CategoryService } from '@vitafy/storefront-api-contracts-fetch';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import { getLayoutData } from '@backend/layoutData';
import useTranslate from '@common/hooks/useTranslate';
import { isStoreCode } from '@common/typeCheckers';
import { reduceProductHit } from '@stories/organisms/HitListView/HitCompact/reducers';
import { reduceHitListViewProps } from '@stories/organisms/HitListView/ReduceProps';
import { ProductList } from '@stories/templates/Search';
import Header from '@stories/templates/Search/Header';
import SEOText from '@stories/templates/Search/SEOText';
import productListDefaultSettings from '@utils/data/ProductListDefaultSettings';
import { getSearchClient, setupRequests } from '@utils/helpers';
import { getChildRequestLogger } from '@utils/logger';
import { ProductListContext, isSortingOrder, useProductList } from '@utils/productList';

import Layout from '../../../components/layout';

import type { LayoutProps } from '../../../components/layout';
import type { NextPageWithLayout } from '../../_app';
import type { HitCompactProps } from '@stories/organisms/HitListView/HitCompact';
import type { ActiveFiltersAndSorting, productsAndAvailableFilters, sortingOrder } from '@utils/productList';
import type { TSearchProductsByCategory } from '@vitafy/search-data-api-contracts';
import type { GetServerSideProps } from 'next';

setupRequests();

const globalDefaultSortingOrder = 'relevance_desc';

type TCategoryByIdRouteParams = {
  categoryId: string;
  store: string;
};

type TReduceProductListResponseSignature = (
  response: TSearchProductsByCategory
) => productsAndAvailableFilters;
export const reduceProductListResponse: TReduceProductListResponseSignature = (response) => {
  const {
    initialStoreState: { aggs, hits, total, widgets }
  } = response;

  const reducedWidgets = widgets
    .map(({ name, ...configuration }) => ({
      name,
      configuration,
      options: aggs[name]
    }))
    .filter((widget) => typeof widget.options !== 'undefined');

  const reducedHits = hits.map(reduceHitListViewProps);

  const state = {
    filterList: reducedWidgets,
    hits: reducedHits,
    total
  };

  return state;
};

export const getServerSideProps: GetServerSideProps<
  { productListInitialState: productsAndAvailableFilters },
  TCategoryByIdRouteParams
> = async (context) => {
  const { params, query, res, req, preview } = context;
  const log = getChildRequestLogger(req, params, query);
  if (!params?.store) {
    log.error('Store should be defined for category page');
    throw new Error('Store should be defined for category page');
  }
  if (!params?.categoryId) {
    log.error('Category id should be defined for category page');
    return {
      notFound: true
    };
  }
  const { categoryId, store: storeCode } = params || {};
  if (!categoryId || !storeCode || !isStoreCode(storeCode)) {
    log.error('Invalid params: categoryId or store');
    throw new Error('Invalid params: categoryId or store');
  }

  const { filter, page, sort } = query as {
    filter: string | undefined;
    page: string | undefined;
    sort: sortingOrder | undefined;
  };

  const parsedFilters = FiltersSchema.safeParse(JSON.parse(filter ?? '{}'));

  const categoryIdNumber = Number.parseInt(categoryId);

  const searchClient = await getSearchClient();

  try {
    const category = await CategoryService.categoryControllerGetCategoryById({
      storeCodeOrId: storeCode,
      categoryId: categoryIdNumber
    });

    const { category_default_filter } = category;
    const categoryDefaultSortingOrder = isSortingOrder(category_default_filter)
      ? category_default_filter
      : globalDefaultSortingOrder;

    const [productListResponse, productsByCategoryResponse] = await Promise.all([
      searchClient.searchProductsByCategory({
        query: {
          storeCode,
          categoryId: categoryIdNumber,
          filters: parsedFilters.success ? parsedFilters.data : undefined,
          page: page ? Number.parseInt(page) : undefined,
          resultsPerPage: 50, // TODO: use variable or make it configurable
          sort: sort ?? categoryDefaultSortingOrder ?? globalDefaultSortingOrder,
          debug: false
        }
      }),
      searchClient.searchProductsByCategory({
        query: {
          storeCode,
          categoryId: categoryIdNumber,
          resultsPerPage: 12
        }
      })
    ]);

    if (productListResponse.status !== 200 || productsByCategoryResponse.status !== 200) {
      throw new Error(
        `Can't search products. Status productListResponse: ${productListResponse.status}. Status productsByCategoryResponse: ${productsByCategoryResponse.status}`
      );
    }

    const productListInitialState = reduceProductListResponse(productListResponse.body);

    if (!preview) {
      // Cache header
      res.setHeader(
        'X-Cache-Tags',
        [...productListInitialState.hits.map((hit) => hit.id), 'ALL_PRODUCTS'].join('|')
      );
    }

    return {
      props: {
        productListInitialState,
        productCarouselItems: productsByCategoryResponse.body.initialStoreState.hits
          .slice(0, 12)
          .map(reduceProductHit),
        category,
        ...(await getLayoutData({
          context,
          storeCode,
          pagePath: category.url,
          pageType: 'category',
          pageMeta: {
            title: category.meta_title ?? category.name,
            metaDescription: category.meta_description,
            metaTitle: category.meta_title,
            metaKeywords: category.meta_keywords,
            metaRobots: category.meta_robots ?? ''
          },
          isDraftMode: preview
        }))
      }
    };
  } catch (error) {
    log.error(error, 'Rendering category page failed');
    // Render 500 page
    throw error;
  }
};

const CompactProductsCarouselLazy = dynamic(
  () => import('@stories/organisms/Carousel/CompactProductsCarousel'),
  { ssr: false }
);

const translations = {
  'Best Products in this category': {
    de_DE: 'Beliebteste Produkte in dieser Kategorie'
  }
};
type TCategoryByIdPageProps = {
  category: CategoryByIDFromDBDto;
  productListInitialState: productsAndAvailableFilters;
  productCarouselItems: Array<HitCompactProps>;
} & LayoutProps;

const CategoryByIdPage: NextPageWithLayout<TCategoryByIdPageProps> = ({
  productListInitialState,
  productCarouselItems,
  category,
  storeCode
}) => {
  const translate = useTranslate(translations);
  const productListRef = useRef<HTMLDivElement>(null);
  const [transitioning, setTransitioning] = useState(false);
  const router = useRouter();

  const fetchNextState = async ({
    activeFilters,
    currentPage,
    currentSortingOrder
  }: ActiveFiltersAndSorting) => {
    const searchClient = await getSearchClient();
    const searchResponse = await searchClient.searchProductsByCategory({
      query: {
        storeCode,
        categoryId: category.id,
        filters: activeFilters,
        page: currentPage,
        sort: currentSortingOrder,
        resultsPerPage: 50, // TODO: use variable or make it configurable
        debug: new URLSearchParams(window.location.search)?.get('debug') === 'alcoholia'
      }
    });
    if (searchResponse.status !== 200) {
      throw new Error(`Can't search products.`);
    }
    return reduceProductListResponse(searchResponse.body);
  };

  const productListState = useProductList({
    initialState: productListInitialState,
    fetchNextState,
    defaultSortingOrder: isSortingOrder(category.category_default_filter)
      ? category.category_default_filter
      : undefined
  });

  useEffect(() => {
    const setTransitioningTrue = () => setTransitioning(true);
    const setTransitioningFalse = () => setTransitioning(false);

    router.events.on('routeChangeStart', setTransitioningTrue);
    router.events.on('routeChangeComplete', setTransitioningFalse);
    router.events.on('routeChangeError', setTransitioningFalse);

    return () => {
      router.events.off('routeChangeStart', setTransitioningTrue);
      router.events.off('routeChangeComplete', setTransitioningFalse);
      router.events.off('routeChangeError', setTransitioningFalse);
    };
    // want to set the event listener only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div data-category-id={category.id} data-category-name={category.name}>
        <Header
          copyText={storeCode === 'de_de' ? undefined : (category.top_cms_block ?? '')}
          imageFilename={category.image ?? undefined}
          title={category.name}
          breadcrumbs={category.breadcrumbs}
        />
        <ProductListContext.Provider
          value={{
            ...productListState,
            currentCategoryId: category.id,
            defaultSettings: productListDefaultSettings,
            scrollContainerRefIntoView: () => productListRef.current?.scrollIntoView({ behavior: 'smooth' })
          }}
        >
          <div ref={productListRef} id="product-list-ref">
            <ProductList transitioning={transitioning} />
            <CompactProductsCarouselLazy
              headline={translate('Best Products in this category')}
              productCategories={[
                {
                  products: productCarouselItems,
                  label: ''
                }
              ]}
            />
          </div>
        </ProductListContext.Provider>
        <SEOText content={category.description_cms_block ?? ''} />
      </div>
    </>
  );
};

CategoryByIdPage.getLayout = function getLayout(page, pageProps) {
  return (
    <Layout {...pageProps} currentCategoryPath={pageProps.category.path} pageType="catalog_category_view_new">
      {page}
    </Layout>
  );
};

export default CategoryByIdPage;
